import React, { Component } from "react";
import PropTypes from "prop-types";
import FilterFieldset from "./filter-fieldset";
import DesignersFilterFieldset from "./designer-filter-fieldset";
import { childrenPropType } from "components/propTypes";

export class FacetFilters extends Component {
  static propTypes = {
    children: childrenPropType,
    expandFilters: PropTypes.func.isRequired,
    filterOptions: PropTypes.objectOf(
      PropTypes.shape({
        displayName: PropTypes.string.isRequired,
        values: PropTypes.arrayOf(
          PropTypes.shape({ displayName: PropTypes.string.isRequired, id: PropTypes.string.isRequired })
        ),
      })
    ),
    filters: PropTypes.object,
    filtersTitle: PropTypes.string,
    getFieldsetHeader: PropTypes.func,
    idSuffix: PropTypes.string,
    isFilterOpen: PropTypes.func,
    isHomeGoodGrid: PropTypes.bool,
    isDeadEndTextSearch: PropTypes.bool,
    onChange: PropTypes.func,
    onClearFilter: PropTypes.func,
    onToggleOpenFilters: PropTypes.func,
    seoKey: PropTypes.string,
    seoPossibleFilters: PropTypes.array,
    showLimitedFilterSet: PropTypes.bool,
    showSizeFilter: PropTypes.bool,
    submitFilters: PropTypes.func,
    renderRTRFilters: PropTypes.func.isRequired,
  };

  static omittedFilterOptions = ["designer", "bodyTypes", "canonicalSizes", "eventType", "ageRanges", "partitionId"];
  static limitedFilterOptions = [
    "epicCategories",
    "occasions",
    "epicUseCaseEvents",
    "epicStylePersonas",
    "colors",
    "length",
    "epicLength",
  ];
  static limitedCategorySet = [
    "dress",
    "gowns",
    "jumpsuitsandrompers",
    "tops",
    "pants",
    "skirts",
    "outerwear",
    "blazer",
    "handbags",
    "jewelry",
    "sunglasses",
  ];
  static limitedOccasionsSet = [
    "weddingguest",
    "formalaffair",
    "forthebride",
    "bridesmaid",
    "party",
    "datenightout",
    "daytimeevent",
    "work",
    "vacation",
  ];
  static seasonDisplayNames = {
    fall_winter: "cold weather",
    spring_summer: "warm weather",
    seasonless: "year-round",
  };
  static className = "facet-filters";

  static filterOrder = [
    "epicCategories",
    "occasions",
    "epicUseCaseEvents",
    "epicStylePersonas",
    "epicFormality",
    "formality",
    "colors",
    "epicPrint",
    "season",
    "epicSeasonality",
    "length",
    "epicLength",
    "epicSleeve",
    "sleeve",
    "maternity",
    "epicMaternity",
    "epicEmbellishments",
    "embellishments",
    "epicNeckline",
    "neckline",
    "designer",
    "hearts",
    "canonicalSizes",
    "similar_to",
    "fit",
  ];

  onSizeChange = filter => {
    this.props.onChange(filter);
    this.props.submitFilters();
  };

  getFieldsetHeader(key) {
    if (this.props.getFieldsetHeader) {
      return this.props.getFieldsetHeader(key);
    }
    return null;
  }

  getEnabledFilters() {
    const filterOptions = { ...this.props.filterOptions };

    const enabledFilters = Object.keys(filterOptions).filter(filterKey => {
      if (this.props.showLimitedFilterSet) {
        return FacetFilters.limitedFilterOptions.includes(filterKey);
      } else {
        return !FacetFilters.omittedFilterOptions.includes(filterKey);
      }
    });

    return enabledFilters.sort((a, b) => FacetFilters.filterOrder.indexOf(a) - FacetFilters.filterOrder.indexOf(b));
  }

  editSeasonCopy = seasonFilterFromBackend => {
    const editedSeasonFilter = JSON.parse(JSON.stringify(seasonFilterFromBackend));

    // Top-level request for seasonality copy to be: WEATHER
    // Warm weather appropriate (spring / summer, seasonless)
    // Cold weather appropriate (fall / winter, seasonless)
    // Year-Round - Seasonless
    editedSeasonFilter.displayName = "Weather";
    editedSeasonFilter.values?.forEach(val => {
      val.displayName = FacetFilters.seasonDisplayNames[val.id];
    });

    return editedSeasonFilter;
  };

  editOccasionsList = occasionsFilterFromBackend => {
    const editedOccasionsFilter = { ...occasionsFilterFromBackend };

    editedOccasionsFilter.values = FacetFilters.limitedOccasionsSet
      .map(occasion => occasionsFilterFromBackend.values?.find(f => f.id === occasion))
      .filter(f => f);

    return editedOccasionsFilter;
  };

  editCategoryList = categoryListFromBackend => {
    const editCategoryList = { ...categoryListFromBackend };

    editCategoryList.values = FacetFilters.limitedCategorySet
      .map(c => categoryListFromBackend.values?.find(f => f.id === c))
      .filter(f => f);

    return editCategoryList;
  };

  /**
   * Apply any client-side changes to the filters as returned from the BE.
   */
  getModifiedFilters(enabledFilters) {
    const { filterOptions, showLimitedFilterSet } = this.props;
    const filterOptionsClone = { ...filterOptions };

    if (enabledFilters.includes("season") && filterOptionsClone.season) {
      filterOptionsClone.season = this.editSeasonCopy(filterOptions.season);
    }

    if (showLimitedFilterSet) {
      if (enabledFilters.includes("epicUseCaseEvents") && filterOptionsClone.epicUseCaseEvents) {
        filterOptionsClone.epicUseCaseEvents = this.editOccasionsList(filterOptionsClone.epicUseCaseEvents);
      }

      if (enabledFilters.includes("epicCategories") && filterOptionsClone.epicCategories) {
        filterOptionsClone.epicCategories = this.editCategoryList(filterOptionsClone.epicCategories);
      }
    }

    return filterOptionsClone;
  }

  renderFilters() {
    const enabledFilters = this.getEnabledFilters();
    const filterOptions = this.getModifiedFilters(enabledFilters);

    return enabledFilters.map(filterKey => {
      const filter = filterOptions[filterKey];

      return (
        <FilterFieldset
          key={filterKey + this.props.idSuffix}
          filterOpen={this.props.isFilterOpen(filterKey)}
          onToggleOpenFilters={this.props.onToggleOpenFilters}
          onChange={this.props.onChange}
          onClear={this.props.onClearFilter}
          keyName={filterKey}
          filters={this.props.filters[filterKey]}
          idSuffix={this.props.idSuffix}
          submitFilters={this.props.submitFilters}
          fieldsetHeader={this.getFieldsetHeader(filterKey)}
          isDeadEndTextSearch={this.props.isDeadEndTextSearch}
          seoKey={this.props.seoKey}
          seoPossibleFilters={this.props.seoPossibleFilters}
          // not clear from FilterFieldset what these props should be
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...filter}
        />
      );
    });
  }

  renderSizeFilter() {
    //This is only used on the Swaps Grid
    if (!this.props.showSizeFilter) {
      return;
    }

    let fieldsetHeader;
    if (this.props.getFieldsetHeader) {
      fieldsetHeader = this.props.getFieldsetHeader("canonicalSizes");
    }

    const {
      filterOptions: { canonicalSizes },
    } = this.props;

    if (canonicalSizes && Object.keys(canonicalSizes).length) {
      return (
        <FilterFieldset
          key={"canonicalSizes" + this.props.idSuffix}
          onToggleOpenFilters={this.props.onToggleOpenFilters}
          filterOpen={this.props.isFilterOpen("canonicalSizes")}
          displayName={"Size"}
          idSuffix={this.props.idSuffix}
          keyName={"canonicalSizes"}
          submitFilters={this.props.submitFilters}
          onChange={this.onSizeChange}
          filters={this.props.filters.canonicalSizes}
          values={canonicalSizes.values}
          fieldsetHeader={fieldsetHeader}
          seoKey={this.props.seoKey}
          seoPossibleFilters={this.props.seoPossibleFilters}
          isDeadEndTextSearch={this.props.isDeadEndTextSearch}
          // not clear from FilterFieldset what these props should be
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...canonicalSizes}
        />
      );
    }
  }

  renderDesignersFilter() {
    if (this.props.isHomeGoodGrid) {
      return null;
    }

    const filterOptions = this.props?.filterOptions ?? {};
    let fieldsetHeader;

    if (this.props.getFieldsetHeader) {
      fieldsetHeader = this.props.getFieldsetHeader("designer");
    }

    if (filterOptions.designer) {
      return (
        <DesignersFilterFieldset
          key={"designer" + this.props.idSuffix}
          filterOpen={this.props.isFilterOpen("designer")}
          onToggleOpenFilters={this.props.onToggleOpenFilters}
          onChange={this.props.onChange}
          submitFilters={this.props.submitFilters}
          onClear={this.props.onClearFilter}
          filters={this.props.filters.designer}
          keyName="designer"
          fieldsetHeader={fieldsetHeader}
          // not clear from DesignersFilterFieldset what these props should be
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...this.props.filterOptions.designer}
          idSuffix={this.props.idSuffix}
        />
      );
    }
  }

  renderExpandFilterButton() {
    const { expandFilters, showLimitedFilterSet } = this.props;

    if (!showLimitedFilterSet) {
      return null;
    }

    return (
      <button className="cta" onClick={expandFilters}>
        Show more
      </button>
    );
  }

  getFiltersTitle() {
    return this.props.filtersTitle ?? "Filters";
  }

  render() {
    return (
      <div className={FacetFilters.className}>
        <div className="h3 mq__mobile-and-tablet">{this.getFiltersTitle()}</div>
        {this.renderSizeFilter()}
        {this.props.renderRTRFilters()}
        {this.renderFilters()}
        {this.renderDesignersFilter()}
        {this.props.children}
        {this.renderExpandFilterButton()}
      </div>
    );
  }
}

export default FacetFilters;
